import React, { useEffect, useState, useMemo } from "react";
import { Grid } from "@mui/material";
import styled from "styled-components";
import { Button, FlexContainer, LottieLoadingIndicator, Text, portalColors } from "src/components";

// Images
import LinkedInIcon from "src/img/noticeboard/linkedin.svg";
import FacebookIcon from "src/img/noticeboard/facebook.svg";
import InstagramIcon from "src/img/noticeboard/instagram.svg";

import AccountsLinked from "./AccountsLinked";

// custom components
import FacebookLogin from "./components/FacebookLoginButton";
import InstagramLogin from "./components/InstagramLoginButton";
import Skeleton from "../Skeleton";
import { AccountLinkingContext } from "./AccountLinkingContext";
import { SocialMediaPlatformColors } from "./utils/Enums";
import { NOTICEBOARD_TABLE_WIDTH } from "../NoticeboardTable";
import LinkedInLogin from "./components/LinkedInLoginButton";
import ConnectingLinkedInLoader from "./components/ConnectingLinkedInLoading";

const UpperControls = styled.div`
    position: relative;
    width: 100%;
    background: ${portalColors.surface};
    height: auto;
    min-height: 5.625rem;
    display: flex;
    align-items: center;
    border-radius: 7px 7px 0 0;
    border: 1px solid ${portalColors.border};
    border-bottom: none;
    box-sizing: border-box;
    padding: 1em;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-end;
        gap: 1rem;
    }
`;

const CloseButton = styled(Button)`
    border-radius: 10px !important;
    border: ${p => p.border || "none"};
    height: 39px;
    width: 124px;
    color: ${p => p.color || portalColors.text};
    background-color: ${p => p.backgroundColor || portalColors.surface};
    box-sizing: border-box;
    box-shadow: 0px 3px 6px #00000029;
    font-size: 0.875rem;
    font-weight: 500;
    padding: ${p => p.padding};
    display: flex;
    align-items: center;
    justify-content: center;
`;


export const SocialMediaButton = styled(Button)`
    border-radius: 10px !important;
    border: ${p => p.border || "none"};
    height: ${p => p.height || "51px"};
    width: ${p => p.width || "250px"};
    color: ${p => p.color || "#fff"};
    background-color: ${p => p.backgroundColor || "#171A22"};
    box-sizing: border-box;
    box-shadow: 0 3px 6px #00000029;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 10px 20px !important;
    display: flex;
    align-items: center;
    justify-content: unset;
    &:disabled {
        cursor: not-allowed;
        opacity: 80%;
    }
`;

export const Container = styled.div`
    margin: 20px 26px 20px 18px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
`;

export const FlexRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const LoadingContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
`;

const SocialMediaLimitationExplainerCard = styled.div`
    max-width: 32.063rem;
    padding: 0px 1rem 0px 1rem;
    background: ${portalColors.surface};
    box-shadow: 0px 3px 6px #00000029;
    border: 1px solid ${portalColors.border};
    border-radius: 7px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`;

const LinkedAccountContainer = ({ isLoading, children }) => {
    return (
        <>
            { isLoading && <Skeleton width="250px" height="51px" animation="wave" /> }
            { !isLoading && children }
        </>
    );
};

const AccountLinkedContainer = ({ isLoading, children }) => {
    if (isLoading) {
        return (
            <LoadingContainer>
                <LottieLoadingIndicator containerHeight="2rem" />
            </LoadingContainer> 
        );
    } else {
        return children;
    }
};

const DisplayConnectFacebook = ({ totalFacebookAccounts, saveFacebookDetails }) => {
    return (
        <>
            {
                totalFacebookAccounts < 4 ?
                    <FacebookLogin onSuccess={(accessToken) => saveFacebookDetails(accessToken)}/>
                    : 
                    <SocialMediaButton disabled="true" width="280px">
                        <img src={FacebookIcon} alt="Facebook Icon" style={{ marginRight: "12px" }}/>
                        Maximum accounts reached
                    </SocialMediaButton>
            }
        </>
    );
};

const DisplayConnectInstagram = ({ totalInstagramAccounts, saveInstagramDetails }) => {
    return (
        <>
            {
                totalInstagramAccounts < 4 ?
                    <InstagramLogin onSuccess={(oauthCode) => saveInstagramDetails(oauthCode)} />
                    : 
                    <SocialMediaButton backgroundColor={SocialMediaPlatformColors.INSTAGRAM} disabled="true" width="280px">
                        <img src={InstagramIcon} alt="Instagram Icon" style={{ marginRight: "12px" }}/>
                        Maximum accounts reached
                    </SocialMediaButton>
            }
        </>
    );
};

const DisplayConnectLinkedIn = ({ totalLinkedInAccounts }) => {
    return (
        <>
            {
                totalLinkedInAccounts < 4 ?
                    <LinkedInLogin />
                    : 
                    <SocialMediaButton backgroundColor={SocialMediaPlatformColors.LINKEDIN} disabled="true" width="280px">
                        <img src={LinkedInIcon} alt="LinkedIn Icon" style={{ marginRight: "12px" }}/>
                        Maximum accounts reached
                    </SocialMediaButton>
            }
        </>
    );
};

const AccountLinkingPage = ({
    user,
    toggleShowAccountLinkingPage,
    retrieveLinkedAccounts,
    saveFacebookDetails,
    saveInstagramDetails,
    revokeSocialMediaAccount,
    handleFacebookReconnect,
    handleInstagramReconnect,
    linkedAccounts,
    linkedAccountsFetched,
    showAccountsLinked,
    connectingLinkedInAccount,
    setConnectingLinkedInAccount
}) => {
    const [totalFacebookAccounts, setTotalFacebookAccounts] = useState(0);
    const [totalInstagramAccounts, setTotalInstagramAccounts] = useState(0);
    const [totalLinkedinAccounts, setTotalLinkedinAccounts] = useState(0);

    const [hasLinkedFacebookAccounts, setHasLinkedFacebookAccounts] = useState(false);
    const [hasLinkedInstagramAccounts, setHasLinkedInstagramAccounts] = useState(false);
    const [hasLinkedLinkedinAccounts, setHasLinkedLinkedinAccounts] = useState(false);

    useEffect(() => {
        if (!connectingLinkedInAccount) {
            retrieveLinkedAccounts();
        }
    }, []);
    
    useEffect(() => {
        setTotalFacebookAccounts((linkedAccounts.Facebook && linkedAccounts.Facebook.length) || 0);
        setTotalInstagramAccounts((linkedAccounts.Instagram && linkedAccounts.Instagram.length) || 0);
        setTotalLinkedinAccounts((linkedAccounts.LinkedIn && linkedAccounts.LinkedIn.length) || 0);
        setHasLinkedFacebookAccounts(linkedAccounts.Facebook && linkedAccounts.Facebook.length > 0);
        setHasLinkedInstagramAccounts(linkedAccounts.Instagram && linkedAccounts.Instagram.length > 0);
        setHasLinkedLinkedinAccounts(linkedAccounts.LinkedIn && linkedAccounts.LinkedIn.length > 0);
    }, [linkedAccounts]);

    const supportedSocialPlatformsHint = useMemo(() => {
        return "e.g. Facebook, Instagram, LinkedIn";
    }, []);

    if (connectingLinkedInAccount) {
        return <ConnectingLinkedInLoader />;
    }

    return (
        <FlexContainer maxWidth={NOTICEBOARD_TABLE_WIDTH}>
            <UpperControls>
                <div style={{ flex: 1, paddingLeft: "1em" }}>
                    <Text size="30px" align="left" weight="700" margin="0" color={portalColors.text}>
                Get Started with Social Account Linking
                    </Text>
                </div>

                <div>
                    <CloseButton
                        border={`1px solid ${portalColors.borderStrong}`}
                        style={{ width: "7.813rem", marginRight: "1em" }}
                        onClick={() => toggleShowAccountLinkingPage()}
                    >
                Close
                    </CloseButton>
                </div>
            </UpperControls>
            <AccountLinkingContext.Provider value={{
                totalFacebookAccounts: totalFacebookAccounts,
                totalInstagramAccounts: totalInstagramAccounts,
                totalLinkedinAccounts: totalLinkedinAccounts,
                hasLinkedFacebookAccounts: hasLinkedFacebookAccounts,
                hasLinkedInstagramAccounts: hasLinkedInstagramAccounts,
                hasLinkedLinkedinAccounts: hasLinkedLinkedinAccounts,
                showAccountsLinked: showAccountsLinked,
            }}>
                <Grid container direction="row" wrap="wrap" style={{ maxWidth: "100%", minHeight: "50rem", border: "1px solid var(--calendar-border, #D8DEEC)", backgroundColor: "var(--calendar-surface, #FFF)", marginBottom: "2rem" }}>
                    <Grid item xs={12} md={6} style={{ borderRight: "1px solid var(--calendar-border, #D8DEEC)" }}> {/* Left Side */}
                        <div style={{ margin: "30px 60px 30px 65px" }}>
                            <Text size="20px" weight="700" margin="0 0 17px 0" align="left" color={portalColors.text}>Link Your Social Accounts with Ease</Text>
                            <Text size="16px" margin="0" align="left" padding="0 6.875rem 0 0" color={portalColors.mutedText}>Connect your Facebook, Instagram, and LinkedIn accounts to seamlessly post to your Me Business Noticeboard and social media platforms at the same time.</Text>

                            <Text size="20px" weight="700" margin="43px 0 17px 0" align="left" color={portalColors.text}>Select Accounts to Link:</Text>

                            <LinkedAccountContainer isLoading={!linkedAccountsFetched}>
                                <DisplayConnectFacebook
                                    totalFacebookAccounts={totalFacebookAccounts}
                                    saveFacebookDetails={saveFacebookDetails}
                                />
                            </LinkedAccountContainer>

                            <Text size="16px" align="left" weight="400" padding="0 6.875rem 0 0" margin="12px 0 26px 0" color={portalColors.mutedText}>
                                Link your Facebook account to share updates directly to your business page alongside your Me Noticeboard posts.
                            </Text>

                            <LinkedAccountContainer isLoading={!linkedAccountsFetched}>
                                <DisplayConnectInstagram
                                    saveInstagramDetails={saveInstagramDetails}
                                    totalInstagramAccounts={totalInstagramAccounts}
                                />
                            </LinkedAccountContainer>

                            <Text size="16px" align="left" weight="400" padding="0 6.875rem 0 0" margin="12px 0 26px 0" color={portalColors.mutedText}>
                                Link your Instagram account to post engaging updates and visuals to your followers while updating your Noticeboard.
                            </Text>

                            <LinkedAccountContainer isLoading={!linkedAccountsFetched}>
                                <DisplayConnectLinkedIn
                                    totalLinkedInAccounts={totalLinkedinAccounts}
                                />
                            </LinkedAccountContainer>

                            <Text size="16px" align="left" weight="400" padding="0 6.875rem 0 0" margin="12px 0 26px 0" color={portalColors.mutedText}>
                                Link your LinkedIn account to share professional updates seamlessly with your network and Noticeboard audience.
                            </Text>

                            <div style={{ marginTop: "66px" }}>
                                <SocialMediaLimitationExplainerCard>
                                    <div style={{ display: "flex", flexDirection: "column", width: "29rem" }}>
                                        <Text size="14px" align="left" weight="700" color="#8E4EB4" style={{ marginBottom: "0" }}>
                                            Shared Post Management
                                        </Text>
                                        <Text size="14px" align="left" weight="400" color="#8E4EB4">
                                            Posts shared from the Me Business platform to linked social media accounts cannot be edited or deleted via the Me Business portal.
                                            To make changes or remove these posts, please manage them directly through the respective social media platform ({supportedSocialPlatformsHint}).
                                        </Text>
                                    </div>
                                </SocialMediaLimitationExplainerCard>
                            </div>
                        </div>
                    </Grid>
                    <Grid item xs={12} md={6}> {/* right side */}
                        <AccountLinkedContainer
                            isLoading={!linkedAccountsFetched}
                        >
                            <AccountsLinked
                                linkedAccounts={linkedAccounts}
                                linkedAccountsFetched={linkedAccountsFetched}
                                revokeSocialMediaAccount={revokeSocialMediaAccount}
                                handleFacebookReconnect={handleFacebookReconnect}
                                handleInstagramReconnect={handleInstagramReconnect}
                            />
                        </AccountLinkedContainer>
                    </Grid>
                </Grid>
            </AccountLinkingContext.Provider>
        </FlexContainer>
    );
};

export default AccountLinkingPage;
